// Book analytics for Insights v2 — everything derived from the workspace, nothing sampled:
// health distribution (+ who is at risk), coverage opportunities per area, portfolio cohorts by
// start year, a 12-week activity calendar and the application funnel.
import { CASE_MAIN_STAGES, addDays, annualNow, isActivePolicy, type Day, type WorkspaceView } from '@vero/domain';
import { coverageProfile, COVERAGE_AREAS, type CoverageArea } from './coverage.ts';
import { relationshipHealth, type Health } from './health.ts';

export interface BookAnalytics {
  health: { band: Health['band']; count: number }[];
  atRisk: { personId: string; score: number; band: Health['band']; annual: number }[];
  gaps: { area: CoverageArea; personIds: string[] }[];
  cohorts: { year: number; annual: number; policies: number }[];
  /** 84 days, oldest first, ending today: completed actions per day */
  activity: { day: Day; count: number }[];
  funnel: { stage: string; count: number }[];
}

export function bookAnalytics(v: WorkspaceView, today: Day): BookAnalytics {
  const clients = v.rows('persons').filter((p) => p.stage === 'client' && !p.archivedAt);
  const dist: Record<Health['band'], number> = { strong: 0, steady: 0, watch: 0, at_risk: 0 };
  const risk: BookAnalytics['atRisk'] = [];
  const gaps = new Map<CoverageArea, string[]>(COVERAGE_AREAS.map((a) => [a, []]));
  for (const c of clients) {
    const h = relationshipHealth(v, c.id, today);
    dist[h.band]++;
    const annual = v.where('policies', 'personId', c.id).filter(isActivePolicy).reduce((s, x) => s + annualNow(v, x, today), 0);
    if (h.band === 'at_risk' || h.band === 'watch') risk.push({ personId: c.id, score: h.score, band: h.band, annual });
    for (const a of coverageProfile(v, c.id, today).gaps) gaps.get(a)!.push(c.id);
  }
  const cohortMap = new Map<number, { annual: number; policies: number }>();
  for (const p of v.rows('policies')) {
    if (!isActivePolicy(p) || !p.startDate) continue;
    const y = Number(p.startDate.slice(0, 4));
    if (!Number.isFinite(y) || y < 1950) continue;
    const e = cohortMap.get(y) ?? { annual: 0, policies: 0 };
    e.annual += annualNow(v, p, today); e.policies++;
    cohortMap.set(y, e);
  }
  const years = [...cohortMap.keys()].sort((a, b) => a - b);
  const cohorts = years.length ? Array.from({ length: years[years.length - 1]! - years[0]! + 1 }, (_, i) => ({ year: years[0]! + i, ...(cohortMap.get(years[0]! + i) ?? { annual: 0, policies: 0 }) })) : [];
  const start = addDays(today, -83);
  const counts = new Map<Day, number>();
  for (const t of v.rows('tasks')) {
    if (t.status !== 'done' || !t.completedAt) continue;
    const d = t.completedAt.slice(0, 10);
    if (d < start || d > today) continue;
    counts.set(d, (counts.get(d) ?? 0) + 1);
  }
  for (const r of v.rows('reviews')) if (r.date >= start && r.date <= today) counts.set(r.date, (counts.get(r.date) ?? 0) + 1);
  const activity = Array.from({ length: 84 }, (_, i) => { const day = addDays(start, i); return { day, count: counts.get(day) ?? 0 }; });
  const cases = v.rows('cases');
  const funnel = CASE_MAIN_STAGES.map((stage, i) => ({ stage, count: cases.filter((c) => { const idx = CASE_MAIN_STAGES.indexOf(c.stage as typeof CASE_MAIN_STAGES[number]); return idx >= i; }).length }));
  return {
    health: (['strong', 'steady', 'watch', 'at_risk'] as const).map((band) => ({ band, count: dist[band] })),
    atRisk: risk.sort((a, b) => a.score - b.score || b.annual - a.annual),
    gaps: COVERAGE_AREAS.map((area) => ({ area, personIds: gaps.get(area)! })).filter((g) => g.personIds.length).sort((a, b) => b.personIds.length - a.personIds.length),
    cohorts, activity, funnel,
  };
}
